import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format, isToday, isYesterday } from 'date-fns';
import { API_URL, getAuthHeader } from '../utils/config';
import useStore from '../store/useStore';

const NOTE_COLORS = [
  { id: 'default', bg: '#1a1f2e', dot: '#6b7280' },
  { id: 'yellow',  bg: '#2a2510', dot: '#f59e0b' },
  { id: 'blue',    bg: '#0f1a2e', dot: '#3b82f6' },
  { id: 'green',   bg: '#0f2318', dot: '#10b981' },
  { id: 'pink',    bg: '#2a1020', dot: '#ec4899' },
  { id: 'purple',  bg: '#1a1030', dot: '#8b5cf6' },
];

const NOTE_COLORS_LIGHT = [
  { id: 'default', bg: '#ffffff', dot: '#6b7280' },
  { id: 'yellow',  bg: '#fffbeb', dot: '#f59e0b' },
  { id: 'blue',    bg: '#eff6ff', dot: '#3b82f6' },
  { id: 'green',   bg: '#f0fdf4', dot: '#10b981' },
  { id: 'pink',    bg: '#fdf2f8', dot: '#ec4899' },
  { id: 'purple',  bg: '#faf5ff', dot: '#8b5cf6' },
];
const getColor = (id, dark = true) => {
  const pal = dark ? NOTE_COLORS : NOTE_COLORS_LIGHT;
  return pal.find(c => c.id === id) || pal[0];
};
const getTheme = (dark) => dark ? {
  appBg: '#0d1117', sidebarBg: '#0d1424', text: '#e6edf3', textArea: '#c9d1d9', text2: '#e5e7eb',
  muted: '#6b7280', faint: '#374151',
  border07: 'rgba(255,255,255,0.07)', border06: 'rgba(255,255,255,0.06)', border08: 'rgba(255,255,255,0.08)', border10: 'rgba(255,255,255,0.1)',
  hover05: 'rgba(255,255,255,0.05)', inputBg: 'rgba(255,255,255,0.04)',
  menuBg: '#1e2433', modalBg: '#161b22', toolbarBg: 'rgba(0,0,0,0.3)',
} : {
  appBg: '#f8fafc', sidebarBg: '#ffffff', text: '#1e293b', textArea: '#334155', text2: '#1e293b',
  muted: '#64748b', faint: '#94a3b8',
  border07: 'rgba(0,0,0,0.08)', border06: 'rgba(0,0,0,0.06)', border08: 'rgba(0,0,0,0.09)', border10: 'rgba(0,0,0,0.12)',
  hover05: 'rgba(0,0,0,0.04)', inputBg: 'rgba(0,0,0,0.04)',
  menuBg: '#ffffff', modalBg: '#ffffff', toolbarBg: 'rgba(248,250,252,0.85)',
};

const fmt = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isToday(d)) return 'Today ' + format(d, 'HH:mm');
  if (isYesterday(d)) return 'Yesterday ' + format(d, 'HH:mm');
  return format(d, 'MMM d, yyyy');
};

function NoteItem({ note, active, onClick, onRename, onDelete, onPin, t }) {
  const col = getColor(note.color);
  const preview = (note.content || '').replace(/\n/g, ' ').trim().substring(0, 70) || 'No content';
  const [menu, setMenu] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(note.title || '');
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setTitle(note.title || '');
  }, [note.title]);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (renaming && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [renaming]);

  const submitRename = () => {
    if (title.trim()) onRename(note.id, title.trim());
    setRenaming(false);
  };

  return (
    <div style={{ margin: '0 8px 4px', borderRadius: 12, border: active ? '1px solid #0d9488' : '1px solid transparent', background: active ? 'rgba(13,148,136,0.12)' : 'transparent', cursor: 'pointer', position: 'relative' }}
      className="group"
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = t.hover05; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      onClick={() => { if (!renaming) onClick(); }}>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: col.dot, flexShrink: 0 }}/>
          {renaming ? (
            <input ref={inputRef} value={title} onChange={e => setTitle(e.target.value)}
              onBlur={submitRename}
              onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') { setTitle(note.title); setRenaming(false); } }}
              onClick={e => e.stopPropagation()}
              style={{ flex: 1, fontSize: 13, fontWeight: 600, background: t.inputBg, border: '1px solid #0d9488', borderRadius: 6, padding: '2px 6px', color: t.text, outline: 'none' }}/>
          ) : (
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: active ? '#2dd4bf' : t.text2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {note.title || 'Untitled Note'}
            </span>
          )}
          {note.is_pinned && <span style={{ fontSize: 10 }}>📌</span>}
          {/* 3-dot menu */}
          <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button onClick={e => { e.stopPropagation(); setMenu(!menu); }}
              style={{ width: 22, height: 22, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.muted, opacity: 0, transition: 'opacity 0.15s' }}
              className="note-menu-btn"
              onMouseEnter={e => e.currentTarget.style.background = t.border10}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 13, height: 13 }}>
                <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
              </svg>
            </button>
            {menu && (
              <div style={{ position: 'absolute', right: 0, top: 26, width: 140, background: t.menuBg, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 999, overflow: 'hidden' }}>
                <button onClick={e => { e.stopPropagation(); setRenaming(true); setMenu(false); onClick(); }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', border: 'none', background: 'transparent', color: t.text2, fontSize: 12, cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = t.border06}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}>
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4z"/>
                  </svg>
                  Rename
                </button>
                <div style={{ height: 1, background: t.border07, margin: '0 8px' }}/>
                <button onClick={e => { e.stopPropagation(); setMenu(false); onPin(note); }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', border: 'none', background: 'transparent', color: t.text2, fontSize: 12, cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = t.border06}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}>
                    <line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14l-1.5-3V7a2 2 0 00-2-2h-7a2 2 0 00-2 2v7L5 17z"/>
                  </svg>
                  {note.is_pinned ? 'Unpin' : 'Pin'}
                </button>
                <div style={{ height: 1, background: t.border07, margin: '0 8px' }}/>
                <button onClick={e => { e.stopPropagation(); setMenu(false); onDelete(note); }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', border: 'none', background: 'transparent', color: '#f87171', fontSize: 12, cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}>
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                  </svg>
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
        <p style={{ fontSize: 11, color: t.muted, marginLeft: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview}</p>
        <p style={{ fontSize: 10, color: t.faint, marginLeft: 13, marginTop: 3 }}>{fmt(note.updated_at)}</p>
      </div>
      <style>{`.group:hover .note-menu-btn { opacity: 1 !important; }`}</style>
    </div>
  );
}

export default function NotesPage() {
  const { darkMode } = useStore();
  const t = getTheme(darkMode);
  const [notes, setNotes] = useState([]);
  const [active, setActive] = useState(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [loading, setLoading] = useState(true);
  const saveRef = useRef(null);
  const titleRef = useRef(null);
  const colorRef = useRef(null);

  useEffect(() => { fetchNotes(); }, []);

  useEffect(() => {
    const h = (e) => { if (colorRef.current && !colorRef.current.contains(e.target)) setShowColorPicker(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const fetchNotes = async () => {
    try {
      const { data } = await axios.get(API_URL + '/notes', { headers: getAuthHeader() });
      const list = data || [];
      setNotes(list);
      if (list.length > 0) setActive(list[0]);
    } catch(e) { toast.error('Failed to load notes'); }
    finally { setLoading(false); }
  };

  const createNote = async () => {
    try {
      const { data } = await axios.post(API_URL + '/notes', { title: 'Untitled Note', content: '', color: 'default' }, { headers: getAuthHeader() });
      setNotes(prev => [data, ...prev]);
      setActive(data);
      setTimeout(() => { if (titleRef.current) { titleRef.current.focus(); titleRef.current.select(); } }, 100);
    } catch(e) { toast.error('Failed to create note'); }
  };

  const autoSave = useCallback((n) => {
    if (saveRef.current) clearTimeout(saveRef.current);
    saveRef.current = setTimeout(async () => {
      saveRef.current = null;
      setSaving(true);
      try {
        await axios.put(API_URL + '/notes/' + n.id, { title: n.title, content: n.content, color: n.color, is_pinned: n.is_pinned }, { headers: getAuthHeader() });
        setNotes(prev => prev.map(x => x.id === n.id ? { ...x, updated_at: new Date().toISOString() } : x));
      } catch(e) {}
      finally { setSaving(false); }
    }, 1500);
  }, []);

  // Save immediately when the user finishes (clicks away / switches field)
  const flushSave = (n) => {
    if (saveRef.current) { clearTimeout(saveRef.current); saveRef.current = null; }
    if (!n || !n.id) return;
    setSaving(true);
    axios.put(API_URL + '/notes/' + n.id, { title: n.title, content: n.content, color: n.color, is_pinned: n.is_pinned }, { headers: getAuthHeader() })
      .then(() => setNotes(prev => prev.map(x => x.id === n.id ? { ...x, updated_at: new Date().toISOString() } : x)))
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const update = (field, value) => {
    const updated = { ...active, [field]: value };
    setActive(updated);
    setNotes(prev => prev.map(n => n.id === updated.id ? updated : n));
    autoSave(updated);
  };

  const deleteNote = async () => {
    try {
      await axios.delete(API_URL + '/notes/' + active.id, { headers: getAuthHeader() });
      const rest = notes.filter(n => n.id !== active.id);
      setNotes(rest);
      setActive(rest[0] || null);
      setShowDelete(false);
      toast.success('Note deleted');
    } catch(e) { toast.error('Failed to delete'); }
  };

  const renameNote = async (id, title) => {
    try {
      await axios.put(API_URL + '/notes/' + id, { title }, { headers: getAuthHeader() });
      setNotes(prev => prev.map(n => n.id === id ? { ...n, title } : n));
      if (active && active.id === id) setActive(prev => ({ ...prev, title }));
    } catch(e) {}
  };

  const togglePin = async (note) => {
    const newPinned = !note.is_pinned;
    try {
      await axios.put(API_URL + '/notes/' + note.id, { title: note.title, content: note.content, color: note.color, is_pinned: newPinned }, { headers: getAuthHeader() });
      setNotes(prev => prev.map(n => n.id === note.id ? { ...n, is_pinned: newPinned } : n));
      if (active && active.id === note.id) setActive(prev => ({ ...prev, is_pinned: newPinned }));
      toast.success(newPinned ? 'Pinned' : 'Unpinned');
    } catch(e) { toast.error('Failed to update'); }
  };

  const copyNote = () => {
    if (!active) return;
    navigator.clipboard.writeText(active.title + '\n\n' + active.content);
    toast.success('Copied to clipboard');
  };

  const filtered = notes.filter(n =>
    (n.title || '').toLowerCase().includes(search.toLowerCase()) ||
    (n.content || '').toLowerCase().includes(search.toLowerCase())
  );
  const pinned = filtered.filter(n => n.is_pinned);
  const unpinned = filtered.filter(n => !n.is_pinned);
  const col = getColor(active ? active.color : 'default', darkMode);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: 'system-ui,-apple-system,sans-serif', background: t.appBg, color: t.text }}>

      {/* SIDEBAR */}
      <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', background: t.sidebarBg, borderRight: '1px solid rgba(255,255,255,0.07)' }}>
        {/* Header */}
        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <a href="/chat" style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: t.hover05, color: t.muted, textDecoration: 'none' }}
                onMouseEnter={e => e.currentTarget.style.background = t.border10}
                onMouseLeave={e => e.currentTarget.style.background = t.hover05}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
              </a>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>My Notes</div>
                <div style={{ fontSize: 11, color: t.muted }}>{notes.length} note{notes.length !== 1 ? 's' : ''}</div>
              </div>
            </div>
            <button onClick={createNote}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#0d9488,#2563eb)', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 13, height: 13 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New
            </button>
          </div>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 10, top: 9, width: 13, height: 13, color: t.muted }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input type="text" placeholder="Search notes..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', paddingLeft: 30, paddingRight: 10, paddingTop: 8, paddingBottom: 8, borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: t.inputBg, color: t.text, fontSize: 12, outline: 'none', boxSizing: 'border-box' }}/>
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', paddingTop: 8 }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <div style={{ width: 24, height: 24, border: '2px solid #0d9488', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: t.muted }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 40, height: 40, margin: '0 auto 12px', opacity: 0.3 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              <p style={{ fontSize: 13, fontWeight: 600 }}>{search ? 'No notes found' : 'No notes yet'}</p>
              {!search && <p style={{ fontSize: 11, marginTop: 4 }}>Click New to get started</p>}
            </div>
          ) : (
            <>
              {pinned.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '4px 16px 6px' }}>📌 Pinned</div>
                  {pinned.map(n => <NoteItem key={n.id} note={n} active={active && active.id === n.id} onClick={() => setActive(n)} onRename={renameNote} onDelete={n2 => { setActive(n2); setShowDelete(true); }} onPin={togglePin} t={t}/>)}
                </div>
              )}
              {unpinned.length > 0 && (
                <div>
                  {pinned.length > 0 && <div style={{ fontSize: 10, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '8px 16px 6px' }}>Notes</div>}
                  {unpinned.map(n => <NoteItem key={n.id} note={n} active={active && active.id === n.id} onClick={() => setActive(n)} onRename={renameNote} onDelete={n2 => { setActive(n2); setShowDelete(true); }} onPin={togglePin} t={t}/>)}
                </div>
              )}
            </>
          )}
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>

      {/* EDITOR */}
      {active ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: col.bg, transition: 'background 0.2s' }}>
          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)', background: t.toolbarBg, backdropFilter: 'blur(8px)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {/* Pin */}
              <button onClick={() => update('is_pinned', !active.is_pinned)} title={active.is_pinned ? 'Unpin' : 'Pin'}
                style={{ width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: active.is_pinned ? 'rgba(245,158,11,0.2)' : 'transparent', color: active.is_pinned ? '#f59e0b' : t.muted }}
                onMouseEnter={e => e.currentTarget.style.background = active.is_pinned ? 'rgba(245,158,11,0.3)' : t.border06}
                onMouseLeave={e => e.currentTarget.style.background = active.is_pinned ? 'rgba(245,158,11,0.2)' : 'transparent'}>
                <svg viewBox="0 0 24 24" fill={active.is_pinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </button>
              {/* Color */}
              <div ref={colorRef} style={{ position: 'relative' }}>
                <button onClick={() => setShowColorPicker(!showColorPicker)}
                  style={{ width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, background: 'transparent', color: t.muted }}
                  onMouseEnter={e => e.currentTarget.style.background = t.border06}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ width: 12, height: 12, borderRadius: '50%', background: col.dot, flexShrink: 0 }}/>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 10, height: 10 }}><polyline points="6 9 12 15 18 9"/></svg>
                </button>
                {showColorPicker && (
                  <div style={{ position: 'absolute', top: 38, left: 0, background: t.menuBg, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 10, display: 'flex', gap: 8, zIndex: 99, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                    {NOTE_COLORS.map(c => (
                      <button key={c.id} onClick={() => { update('color', c.id); setShowColorPicker(false); }}
                        style={{ width: 24, height: 24, borderRadius: '50%', border: active.color === c.id ? '2px solid #0d9488' : '2px solid transparent', cursor: 'pointer', background: c.dot, outline: active.color === c.id ? '2px solid rgba(13,148,136,0.4)' : 'none', outlineOffset: 2 }}/>
                    ))}
                  </div>
                )}
              </div>
              {/* Copy */}
              <button onClick={copyNote} title="Copy note"
                style={{ width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', color: t.muted }}
                onMouseEnter={e => e.currentTarget.style.background = t.border06}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {saving ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#0d9488' }}>
                  <div style={{ width: 10, height: 10, border: '1.5px solid #0d9488', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
                  Saving...
                </div>
              ) : (
                <span style={{ fontSize: 11, color: t.muted }}>Saved {fmt(active.updated_at)}</span>
              )}
              <button onClick={() => setShowDelete(true)} title="Delete note"
                style={{ width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', color: t.muted }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = '#f87171'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = t.muted; }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ maxWidth: 1100, width: '100%', margin: '0 auto', padding: '32px 48px 16px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, boxSizing: 'border-box' }}>
              <input ref={titleRef} type="text" value={active.title} onChange={e => update('title', e.target.value)} onBlur={() => flushSave(active)}
                placeholder="Note title..."
                style={{ width: '100%', fontSize: 32, fontWeight: 800, color: t.text, background: 'transparent', border: 'none', outline: 'none', marginBottom: 6, letterSpacing: '-0.5px', lineHeight: 1.2 }}/>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28, paddingBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <span style={{ fontSize: 12, color: t.muted }}>{fmt(active.created_at)}</span>
                {active.is_pinned && <span style={{ fontSize: 11, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>📌 Pinned</span>}
              </div>
              <textarea value={active.content} onChange={e => update('content', e.target.value)} onBlur={() => flushSave(active)}
                placeholder={'Start writing your note here...\n\nYou can jot down ideas, meeting notes, important information, tasks, or anything you want to keep handy.'}
                style={{ width: '100%', flex: 1, minHeight: 120, overflowY: 'auto', background: 'transparent', border: 'none', outline: 'none', color: t.textArea, fontSize: 15, lineHeight: 1.8, resize: 'none', fontFamily: 'inherit' }}/>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: t.appBg }}>
          <div style={{ width: 72, height: 72, borderRadius: 20, background: 'linear-gradient(135deg,rgba(13,148,136,0.2),rgba(37,99,235,0.2))', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, border: '1px solid rgba(13,148,136,0.2)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 36, height: 36 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: t.text, marginBottom: 8 }}>Your Personal Notes</h2>
          <p style={{ fontSize: 13, color: t.muted, textAlign: 'center', maxWidth: 280, lineHeight: 1.6, marginBottom: 24 }}>Capture ideas, meeting notes, and important information — all in one secure place.</p>
          <button onClick={createNote}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#0d9488,#2563eb)', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 15px rgba(13,148,136,0.3)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Create Your First Note
          </button>
        </div>
      )}

      {/* Delete Modal */}
      {showDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: t.modalBg, borderRadius: 20, padding: 28, width: '100%', maxWidth: 360, border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 24, height: 24 }}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
            </div>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: t.text, textAlign: 'center', marginBottom: 8 }}>Delete Note?</h3>
            <p style={{ fontSize: 13, color: t.muted, textAlign: 'center', marginBottom: 24 }}>"{active && active.title}" will be permanently deleted. This cannot be undone.</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowDelete(false)}
                style={{ flex: 1, padding: '10px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: t.text, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={deleteNote}
                style={{ flex: 1, padding: '10px', borderRadius: 12, border: 'none', background: '#ef4444', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
